/* Companion integration card — Feb-2026.
 *
 * Admin surface for the cross-app plumbing set up in
 * routes_companion_admin.py + routes_admin_companion.py:
 *   • view + rotate the shared X-Service-Key (DB override wins over env)
 *   • view + rotate the shared JWT signing secret
 *   • see the last-pushed Companion manifest (base URL, endpoints, events)
 *   • fire a test event through the real fan-out plumbing
 *   • ping the Companion's health endpoint
 */
import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  RefreshCcw, Copy, Save, Trash2, Rocket, Radio, Loader2, Link2, Shield,
  KeyRound, Zap, Globe, Activity, ShieldCheck, ShieldAlert, MessageSquare,
  AlertTriangle, X, BookOpen, Sparkles, Slack as SlackIcon, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import ReaderSyncHealthWidget from '../ReaderSyncHealthWidget';
import CertRotationRunbook from '../CertRotationRunbook';

function StatusChip({ src, configured }) {
  if (!configured) {
    return <span className="rounded-full bg-red-500/15 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider text-red-600">UNSET</span>;
  }
  if (src === 'db-override') {
    return <span className="rounded-full bg-emerald-500/15 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">DB OVERRIDE</span>;
  }
  return <span className="rounded-full bg-blue-500/15 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">FROM ENV</span>;
}

function copyToClipboard(v) {
  try {
    navigator.clipboard.writeText(v);
    toast.success('Copied to clipboard.');
  } catch {
    toast.error('Copy failed. Select the text and copy manually.');
  }
}

// iter-cert-pin-diff — Confirmation modal showing added/dropped/unchanged
// fingerprints before overwriting the pin. Requires typing ROTATE to
// prevent an accidental one-click rotation from a stale tab.
function CertPinDiffDialog({ diff, onCancel, onConfirm, pinning }) {
  const [confirmText, setConfirmText] = React.useState('');
  const [showRunbook, setShowRunbook] = React.useState(false);
  if (!diff) return null;
  const canConfirm = diff.bootstrap || confirmText.trim().toUpperCase() === 'ROTATE';
  const heading = diff.bootstrap
    ? 'Bootstrap fingerprint pin'
    : diff.in_sync
      ? 'Fingerprints already match the pin'
      : 'Reader fingerprints have drifted';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="admin-reader-cert-pin-diff-dialog">
      <div className="w-full max-w-2xl rounded-md border border-border bg-background shadow-xl">
        <div className="flex items-start gap-2 border-b border-border px-4 py-3">
          <AlertTriangle className={`mt-0.5 h-5 w-5 shrink-0 ${diff.in_sync ? 'text-emerald-600' : 'text-amber-600'}`} />
          <div className="flex-1">
            <div className="text-[13px] font-semibold">{heading}</div>
            <div className="text-[10.5px] text-muted-foreground break-all">Manifest: {diff.manifest_url}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setShowRunbook(true)} className="h-7 gap-1 text-[11px]" data-testid="admin-reader-cert-pin-diff-runbook-btn">
            <BookOpen className="h-3 w-3" /> Runbook
          </Button>
          <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-4 text-[11.5px]">
          {diff.added.length > 0 && (
            <div className="mb-3">
              <div className="mb-1 text-[11px] font-semibold text-emerald-700">Newly added ({diff.added.length})</div>
              <ul className="space-y-1 rounded-sm border border-emerald-500/30 bg-emerald-500/5 p-2 font-mono-stat text-[10.5px]">
                {diff.added.map(fp => <li key={`a-${fp}`} className="break-all">+ {fp}</li>)}
              </ul>
            </div>
          )}
          {diff.dropped.length > 0 && (
            <div className="mb-3">
              <div className="mb-1 text-[11px] font-semibold text-rose-700">Missing / dropped ({diff.dropped.length})</div>
              <ul className="space-y-1 rounded-sm border border-rose-500/30 bg-rose-500/5 p-2 font-mono-stat text-[10.5px]">
                {diff.dropped.map(fp => <li key={`d-${fp}`} className="break-all">− {fp}</li>)}
              </ul>
            </div>
          )}
          {diff.unchanged.length > 0 && (
            <div className="mb-3">
              <div className="mb-1 text-[11px] font-semibold text-muted-foreground">Unchanged ({diff.unchanged.length})</div>
              <ul className="space-y-1 rounded-sm border border-border p-2 font-mono-stat text-[10.5px] text-muted-foreground">
                {diff.unchanged.map(fp => <li key={`u-${fp}`} className="break-all">= {fp}</li>)}
              </ul>
            </div>
          )}
          {diff.bootstrap && (
            <div className="rounded-sm border border-primary/40 bg-primary/5 p-2 text-[11px]">
              No pin exists yet — pinning now will accept the current fingerprint set as the baseline. No typed confirmation required.
            </div>
          )}
          {!diff.bootstrap && !diff.in_sync && (
            <div className="mt-3">
              <div className="mb-1 text-[11px] font-semibold">Type <code className="rounded bg-muted px-1">ROTATE</code> to confirm the pin overwrite</div>
              <Input
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="ROTATE"
                className="h-8 font-mono-stat"
                data-testid="admin-reader-cert-pin-diff-confirm-input"
              />
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <Button size="sm" variant="ghost" onClick={onCancel} data-testid="admin-reader-cert-pin-diff-cancel">Cancel</Button>
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={!canConfirm || pinning}
            data-testid="admin-reader-cert-pin-diff-confirm"
          >
            {pinning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
            {diff.bootstrap ? 'Bootstrap pin' : 'Overwrite pin'}
          </Button>
        </div>
      </div>
      <CertRotationRunbook open={showRunbook} onClose={() => setShowRunbook(false)} />
    </div>
  );
}

// iter-companion-cert-pin — inline row for the Reader universal-link
// fingerprint pin. Kept as a small sibling component so the parent
// stays legible.
function ReaderCertPinRow() {
  const [state, setState] = React.useState(null);
  const [pinning, setPinning] = React.useState(false);
  const [diff, setDiff] = React.useState(null);
  const [slackPinging, setSlackPinging] = React.useState(false);

  const load = async () => {
    try { setState(await adminApi.readerCert.status()); } catch { setState(null); }
  };
  React.useEffect(() => { load(); }, []);

  const openDiff = async () => {
    try {
      const d = await adminApi.readerCert.diff();
      setDiff(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to fetch Reader manifest.');
    }
  };

  const confirmPin = async () => {
    setPinning(true);
    try {
      const r = await adminApi.readerCert.pin();
      toast.success(`Pinned ${r?.fingerprints?.length || 0} fingerprint(s).`);
      setDiff(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pin failed.');
    } finally { setPinning(false); }
  };

  const slackTest = async () => {
    setSlackPinging(true);
    try {
      const r = await adminApi.readerCert.slackTestPing();
      toast.success(r?.note || 'Slack test ping sent.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Slack test ping failed.');
    } finally { setSlackPinging(false); }
  };

  if (!state) return null;
  const drift = state.last_status === 'drift';
  const Icon = drift ? ShieldAlert : ShieldCheck;
  return (
    <div className="mt-3 rounded-md border border-border p-3" data-testid="admin-reader-cert-pin">
      <div className="flex items-start gap-2">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${drift ? 'text-rose-600' : 'text-emerald-600'}`} />
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold">
            Reader Universal-Link Fingerprint
            <span className={`ml-2 rounded-full px-2 py-[1px] text-[9.5px] font-semibold uppercase tracking-wider ${drift ? 'bg-rose-500/15 text-rose-700' : 'bg-emerald-500/15 text-emerald-700'}`}>
              {state.last_status || 'unchecked'}
            </span>
          </div>
          <div className="mt-1 text-[10.5px] text-muted-foreground break-all">
            <span className="text-muted-foreground">Manifest:</span> {state.manifest_url}
          </div>
          <div className="mt-1 text-[10.5px]">
            <span className="text-muted-foreground">Pinned ({(state.pinned_fingerprints || []).length}):</span>{' '}
            {(state.pinned_fingerprints || []).length ? (
              <code className="font-mono-stat text-[10px]">{(state.pinned_fingerprints || []).slice(0, 3).join(', ')}{state.pinned_fingerprints.length > 3 ? '…' : ''}</code>
            ) : <em className="text-muted-foreground">not pinned yet</em>}
          </div>
          {state.pinned_at && (
            <div className="text-[10px] text-muted-foreground">
              Pinned {new Date(state.pinned_at).toLocaleString()}
              {state.last_check_at && <> · Last check {new Date(state.last_check_at).toLocaleString()}</>}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button size="sm" onClick={openDiff} disabled={pinning} data-testid="admin-reader-cert-pin-btn">
            {pinning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
            Pin current
          </Button>
          <Button size="sm" variant="ghost" onClick={slackTest} disabled={slackPinging} data-testid="admin-reader-cert-slack-test-btn" className="h-7 gap-1 text-[11px]">
            {slackPinging ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageSquare className="h-3 w-3" />}
            Send Slack test ping
          </Button>
        </div>
      </div>
      <CertPinDiffDialog
        diff={diff}
        onCancel={() => setDiff(null)}
        onConfirm={confirmPin}
        pinning={pinning}
      />
    </div>
  );
}



function SlackWebhookRow() {
  const [state, setState] = React.useState(null);
  const [input, setInput] = React.useState('');
  const [busy,  setBusy]  = React.useState(false);
  const load = async () => {
    try { setState(await adminApi.slackWebhook.get()); } catch { setState(null); }
  };
  React.useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await adminApi.slackWebhook.set(input.trim());
      toast.success('Slack webhook saved.');
      setInput(''); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try {
      await adminApi.slackWebhook.clear();
      toast.success('DB override cleared. Falling back to env var (if set).');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear failed.');
    } finally { setBusy(false); }
  };
  if (!state) return null;
  return (
    <div className="mt-3 rounded-md border border-border p-3" data-testid="admin-slack-webhook-card">
      <div className="flex items-start gap-2">
        <SlackIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold">
            Ops Slack Webhook
            <span className={`ml-2 rounded-full px-2 py-[1px] text-[9.5px] font-semibold uppercase tracking-wider ${
              state.configured
                ? (state.source === 'db-override'
                    ? 'bg-emerald-500/15 text-emerald-700'
                    : 'bg-blue-500/15 text-blue-700')
                : 'bg-red-500/15 text-red-600'
            }`}>
              {state.configured ? state.source.toUpperCase() : 'UNSET'}
            </span>
          </div>
          <div className="mt-1 text-[10.5px] text-muted-foreground">
            DB override for <code>SLACK_WEBHOOK_URL</code> — used by the cert-rotation cron, Stripe watchdog, and reader-sync-health alerts.
            {state.configured && <> A webhook is configured.</>}
            {' '}Env {state.env_present ? 'present' : 'unset'}.
          </div>
        </div>
      </div>
      <div className="mt-2 flex gap-2">
        <Input
          type="password"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="https://hooks.slack.com/services/AAA/BBB/CCC"
          className="h-8 max-w-xl font-mono-stat text-[11px]"
          data-testid="admin-slack-webhook-input"
        />
        <Button
          size="sm"
          onClick={save}
          disabled={busy || !input.trim().startsWith('https://hooks.slack.com/')}
          className="h-8 gap-1.5"
          data-testid="admin-slack-webhook-save"
        >
          <Save className="h-3.5 w-3.5" /> Save / Rotate
        </Button>
        {state.db_override_set && (
          <Button
            size="sm"
            variant="ghost"
            onClick={clear}
            disabled={busy}
            className="h-8 gap-1.5 text-muted-foreground"
            data-testid="admin-slack-webhook-clear"
          >
            <Trash2 className="h-3.5 w-3.5" /> Clear override
          </Button>
        )}
      </div>
    </div>
  );
}

function AiSyncRow() {
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [lastResult, setLastResult] = React.useState(null);
  const [health, setHealth] = React.useState(null);
  const [drafts, setDrafts] = React.useState(null);
  const [draftForm, setDraftForm] = React.useState({
    source_id: '', headline: '', author_byline: '', blurb: '',
    link_url: '', genre_slug: '', pinned: true, published: true,
  });
  const [editingDraft, setEditingDraft] = React.useState(null);
  const load = async () => {
    try { setPreview(await adminApi.aiSync.preview()); } catch { setPreview(null); }
    try { setHealth(await adminApi.aiSync.health()); } catch { setHealth(null); }
    try { setDrafts((await adminApi.aiSync.listDrafts()).drafts || []); } catch { setDrafts([]); }
  };
  React.useEffect(() => { load(); }, []);
  const run = async (label, fn) => {
    setBusy(true); setLastResult(null);
    try {
      const r = await fn();
      setLastResult({ label, ...r });
      if (r.ok) toast.success(`${label} synced.`);
      else toast.error(`${label} finished with errors — see result.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed.`);
    } finally { setBusy(false); }
  };
  const saveDraft = async () => {
    if (!draftForm.source_id.trim() || !draftForm.headline.trim()) {
      toast.error('Source ID and Headline are required.'); return;
    }
    setBusy(true);
    try {
      await adminApi.aiSync.saveDraft({
        ...draftForm,
        author_byline: draftForm.author_byline || null,
        blurb:         draftForm.blurb         || null,
        link_url:      draftForm.link_url      || null,
        genre_slug:    draftForm.genre_slug    || null,
      });
      toast.success('Headline draft saved.');
      setDraftForm({ source_id: '', headline: '', author_byline: '', blurb: '',
                       link_url: '', genre_slug: '', pinned: true, published: true });
      setEditingDraft(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };
  const editDraft = (d) => {
    setEditingDraft(d.source_id);
    setDraftForm({
      source_id: d.source_id, headline: d.headline,
      author_byline: d.author_byline || '', blurb: d.blurb || '',
      link_url: d.link_url || '', genre_slug: d.genre_slug || '',
      pinned: d.pinned ?? true, published: d.published ?? true,
    });
  };
  const removeDraft = async (id) => {
    setBusy(true);
    try {
      await adminApi.aiSync.deleteDraft(id);
      toast.success('Draft deleted.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    } finally { setBusy(false); }
  };
  if (!preview) return null;
  const latest = health?.latest;
  const spark = health?.sparkline || [];
  const maxBooks = Math.max(1, ...spark.map((d) => d.books || 0));
  return (
    <div className="mt-3 rounded-md border border-border p-3" data-testid="admin-ai-sync-card">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold">
            Mirror bookstore to Reader library
            <span className={`ml-2 rounded-full px-2 py-[1px] text-[9.5px] font-semibold uppercase tracking-wider ${
              preview.configured ? 'bg-emerald-500/15 text-emerald-700' : 'bg-red-500/15 text-red-600'
            }`}>
              {preview.configured ? 'READY' : 'READER UNSET'}
            </span>
          </div>
          <div className="mt-1 text-[10.5px] text-muted-foreground">
            Idempotent push per <code>/app/memory/CHILD_APP_AI_ASSISTED_GENRES_SCRIPT.md</code>. Nightly auto-mirror at ~<b>03:00 UTC</b>; manual buttons below force an immediate run. Books de-duped on <code>source_book_id</code>; <code>ai_assisted</code> routes each title to the correct child-app stack.
          </div>
          <div className="mt-1 flex flex-wrap gap-3 text-[10.5px] text-muted-foreground">
            <span><b className="text-foreground">{preview.genre_count}</b> genre(s)</span>
            <span><b className="text-foreground">{preview.regular_book_count ?? 0}</b> regular book(s)</span>
            <span><b className="text-foreground">{preview.ai_book_count ?? preview.book_count}</b> AI-assisted book(s)</span>
            <span><b className="text-foreground">{preview.headline_count}</b> headline(s)</span>
          </div>
        </div>
      </div>

      {/* iter-auto-mirror — health widget */}
      {health && (
        <div className="mt-2 rounded-sm border border-border/60 bg-muted/30 p-2 text-[10.5px]"
             data-testid="admin-ai-sync-health">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-[1px] text-[9.5px] font-semibold uppercase tracking-wider ${
              latest?.ok ? 'bg-emerald-500/15 text-emerald-700'
                          : (latest ? 'bg-rose-500/15 text-rose-700'
                                     : 'bg-amber-500/15 text-amber-700')
            }`}>
              {latest?.ok ? <><CheckCircle2 className="h-3 w-3" /> Healthy</>
                            : (latest ? <><AlertTriangle className="h-3 w-3" /> Failed</>
                                        : <><Activity className="h-3 w-3" /> No runs yet</>)}
            </span>
            <span>Last run: <b className="text-foreground">{latest ? new Date(latest.finished_at).toLocaleString() : '—'}</b></span>
            <span>Trigger: <b className="text-foreground">{latest?.trigger ?? '—'}</b></span>
            <span>14-day: <b className="text-foreground">{health.successful_14d}/{health.runs_14d}</b> ok · <b className="text-foreground">{health.books_synced_14d}</b> books</span>
          </div>
          {latest && (
            <div className="mt-1 text-muted-foreground">
              +{latest.created_count} new · ↻{latest.updated_count} updated · ✗{latest.failed_count} failed · {latest.duration_ms}ms
              {latest.error && <span className="ml-2 text-rose-700"> · {latest.error}</span>}
            </div>
          )}
          {/* iter-sparkline — 14-day mini bar chart */}
          {spark.length > 0 && (
            <div className="mt-1 flex h-10 items-end gap-[2px]" data-testid="admin-ai-sync-sparkline"
                 title="Last 14 days of successful mirror runs (bar height = books synced)">
              {spark.map((d) => {
                const h = Math.max(2, Math.round((d.books / maxBooks) * 32));
                const bg = d.fail ? 'bg-rose-400' : (d.ok ? 'bg-emerald-500' : 'bg-muted-foreground/25');
                return (
                  <div key={d.day} className={`w-2 rounded-sm ${bg}`} style={{ height: `${h}px` }}
                       title={`${d.day} · ok=${d.ok} fail=${d.fail} books=${d.books}`} />
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={() => run('Genres', adminApi.aiSync.genres)}
          disabled={busy || !preview.configured}
          className="h-8 gap-1.5"
          data-testid="admin-ai-sync-genres-btn"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Sync Genres
        </Button>
        <Button
          size="sm"
          onClick={() => run('Regular books', () => adminApi.aiSync.books('regular'))}
          disabled={busy || !preview.configured || !preview.regular_book_count}
          className="h-8 gap-1.5"
          data-testid="admin-ai-sync-regular-books-btn"
        >
          <BookOpen className="h-3.5 w-3.5" /> Sync Regular Books
        </Button>
        <Button
          size="sm"
          onClick={() => run('AI books', () => adminApi.aiSync.books('ai'))}
          disabled={busy || !preview.configured || !(preview.ai_book_count ?? preview.book_count)}
          className="h-8 gap-1.5"
          data-testid="admin-ai-sync-ai-books-btn"
        >
          <Sparkles className="h-3.5 w-3.5" /> Sync AI Books
        </Button>
        <Button
          size="sm"
          onClick={() => run('Headlines', adminApi.aiSync.headlines)}
          disabled={busy || !preview.configured || !preview.headline_count}
          className="h-8 gap-1.5"
          data-testid="admin-ai-sync-headlines-btn"
        >
          <Radio className="h-3.5 w-3.5" /> Sync Headlines
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => run('Mirror everything', () => adminApi.aiSync.all('all'))}
          disabled={busy || !preview.configured}
          className="h-8 gap-1.5"
          data-testid="admin-ai-sync-all-btn"
        >
          <Rocket className="h-3.5 w-3.5" /> Mirror Everything
        </Button>
      </div>

      {/* iter-headline-composer */}
      <details className="mt-3 rounded-sm border border-border/60 bg-muted/20 p-2" data-testid="admin-ai-headline-composer">
        <summary className="cursor-pointer select-none text-[11.5px] font-semibold">
          AI Headline Composer <span className="ml-2 rounded-full bg-muted px-1.5 text-[9.5px] font-normal text-muted-foreground">{drafts?.length ?? 0} saved</span>
        </summary>
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
          <Input placeholder="source_id (unique, idempotent)" value={draftForm.source_id}
                 onChange={(e) => setDraftForm((f) => ({...f, source_id: e.target.value}))}
                 disabled={!!editingDraft}
                 className="h-8 text-[11px]" data-testid="admin-headline-source-id" />
          <Input placeholder="Headline text" value={draftForm.headline}
                 onChange={(e) => setDraftForm((f) => ({...f, headline: e.target.value}))}
                 className="h-8 text-[11px]" data-testid="admin-headline-text" />
          <Input placeholder="Author byline" value={draftForm.author_byline}
                 onChange={(e) => setDraftForm((f) => ({...f, author_byline: e.target.value}))}
                 className="h-8 text-[11px]" data-testid="admin-headline-byline" />
          <Input placeholder="Genre slug (e.g. dark-romance)" value={draftForm.genre_slug}
                 onChange={(e) => setDraftForm((f) => ({...f, genre_slug: e.target.value}))}
                 className="h-8 text-[11px]" data-testid="admin-headline-genre" />
          <Input placeholder="Blurb (max 1200)" value={draftForm.blurb}
                 onChange={(e) => setDraftForm((f) => ({...f, blurb: e.target.value}))}
                 className="col-span-full h-8 text-[11px]" data-testid="admin-headline-blurb" />
          <Input placeholder="Link URL (deep-link into the Loom or store)" value={draftForm.link_url}
                 onChange={(e) => setDraftForm((f) => ({...f, link_url: e.target.value}))}
                 className="col-span-full h-8 text-[11px]" data-testid="admin-headline-link" />
          <div className="col-span-full flex flex-wrap items-center gap-3 text-[10.5px]">
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={draftForm.pinned}
                     onChange={(e) => setDraftForm((f) => ({...f, pinned: e.target.checked}))}
                     data-testid="admin-headline-pinned" />
              Pinned
            </label>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={draftForm.published}
                     onChange={(e) => setDraftForm((f) => ({...f, published: e.target.checked}))}
                     data-testid="admin-headline-published" />
              Published
            </label>
            <div className="ml-auto flex gap-2">
              {editingDraft && (
                <Button size="sm" variant="ghost" className="h-7 text-[10.5px]"
                        onClick={() => { setEditingDraft(null); setDraftForm({source_id:'',headline:'',author_byline:'',blurb:'',link_url:'',genre_slug:'',pinned:true,published:true}); }}>
                  Cancel
                </Button>
              )}
              <Button size="sm" onClick={saveDraft} disabled={busy}
                      className="h-7 gap-1 text-[10.5px]"
                      data-testid="admin-headline-save-btn">
                <Save className="h-3 w-3" /> {editingDraft ? 'Update' : 'Save'} draft
              </Button>
              <Button size="sm" variant="outline" onClick={() => run('Headlines', adminApi.aiSync.headlines)}
                      disabled={busy || !preview.configured || !(drafts?.length)}
                      className="h-7 gap-1 text-[10.5px]"
                      data-testid="admin-headline-push-btn">
                <Rocket className="h-3 w-3" /> Push all to Reader
              </Button>
            </div>
          </div>
        </div>
        {(drafts?.length ?? 0) > 0 && (
          <div className="mt-2 border-t border-border/60 pt-2">
            {drafts.map((d) => (
              <div key={d.source_id}
                   className="flex items-center gap-2 py-1 text-[11px] border-b border-border/40 last:border-b-0"
                   data-testid={`admin-headline-row-${d.source_id}`}>
                <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${d.pinned ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{d.headline}</div>
                  <div className="truncate text-[10px] text-muted-foreground">
                    <code>{d.source_id}</code>
                    {d.author_byline && <> · {d.author_byline}</>}
                    {d.genre_slug && <> · {d.genre_slug}</>}
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]"
                        onClick={() => editDraft(d)}>Edit</Button>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-rose-700"
                        onClick={() => removeDraft(d.source_id)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </details>

      {lastResult && (
        <div className="mt-2 rounded-sm border border-border/60 bg-muted/40 p-2 text-[10.5px]"
             data-testid="admin-ai-sync-result">
          <div className={`font-semibold ${lastResult.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
            [{lastResult.label}] {lastResult.ok ? 'OK' : 'Errors detected'}
          </div>
          <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono-stat text-[10px] text-muted-foreground">
            {JSON.stringify(lastResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function CompanionIntegrationCard() {
  const [state,   setState]   = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [skInput, setSkInput] = useState('');
  const [jwtInput,setJwtInput]= useState('');
  const [freshKey,setFreshKey]= useState(null);  // {field, value}
  const [testEvent,setTestEvent] = useState('book.published');
  const [appUrlInput,   setAppUrlInput]   = useState('');
  const [appKeyInput,   setAppKeyInput]   = useState('');
  const [appDiag,       setAppDiag]       = useState(null);

  const reload = async () => {
    try { setState(await adminApi.companionIntegrationStatus()); }
    catch { toast.error('Could not load Companion integration status.'); }
  };
  useEffect(() => { reload(); }, []);

  const withBusy = async (fn) => {
    setBusy(true);
    try { await fn(); }
    finally { setBusy(false); }
  };

  const rotateSk = () => withBusy(async () => {
    const r = await adminApi.companionRotateServiceKey();
    setFreshKey(r);
    toast.success('New X-Service-Key generated. Copy it now.');
    reload();
  });
  const rotateJwt = () => withBusy(async () => {
    const r = await adminApi.companionRotateJwtSecret();
    setFreshKey(r);
    toast.success('New JWT signing secret generated. Copy it now.');
    reload();
  });
  const saveSk = () => withBusy(async () => {
    await adminApi.companionSetServiceKey(skInput.trim());
    toast.success('X-Service-Key saved.');
    setSkInput(''); reload();
  });
  const saveJwt = () => withBusy(async () => {
    await adminApi.companionSetJwtSecret(jwtInput.trim());
    toast.success('JWT secret saved.');
    setJwtInput(''); reload();
  });
  const clearSk = () => withBusy(async () => {
    try {
      await adminApi.companionClearServiceKey();
      toast.success('DB override cleared. Falling back to env var.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clear.');
    }
  });
  const clearJwt = () => withBusy(async () => {
    try {
      await adminApi.companionClearJwtSecret();
      toast.success('DB override cleared. Falling back to env var.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clear.');
    }
  });
  const fireEvent = () => withBusy(async () => {
    const r = await adminApi.companionFireTestEvent(testEvent, {source: 'admin-panel-test'});
    toast.success(`Event fired — ${r.fired || 0} subscriber(s) notified.`);
  });
  const pingCompanion = () => withBusy(async () => {
    try {
      const r = await adminApi.companionPing();
      if (r.ok) toast.success(`Companion reachable at ${r.url} (${r.status}).`);
      else toast.error('Companion did not respond on any probed path.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ping failed.');
    }
  });
  const saveAppUrl = () => withBusy(async () => {
    try {
      await adminApi.companionSetAppUrl(appUrlInput.trim());
      toast.success('Companion App URL saved.');
      setAppUrlInput(''); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed.'); }
  });
  const saveAppKey = () => withBusy(async () => {
    try {
      await adminApi.companionSetAppApiKey(appKeyInput.trim());
      toast.success('Companion App API key saved.');
      setAppKeyInput(''); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed.'); }
  });
  const clearAppUrl = () => withBusy(async () => {
    try {
      await adminApi.companionClearAppUrl();
      toast.success('URL override cleared. Falling back to env.');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not clear.'); }
  });
  const clearAppKey = () => withBusy(async () => {
    try {
      await adminApi.companionClearAppApiKey();
      toast.success('API key override cleared. Falling back to env.');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not clear.'); }
  });
  const runAppDiag = () => withBusy(async () => {
    try {
      const r = await adminApi.companionAppDiagnostics();
      setAppDiag(r);
      if (r.ok) toast.success('Companion App reachable — service key accepted.');
      else toast.error(`Companion App unreachable — ${r.stage || 'error'}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Diagnostics failed.'); }
  });

  if (!state) {
    return (
      <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="admin-companion-integration">
        <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading Companion integration status…
        </div>
      </div>
    );
  }

  const sk  = state.secrets.service_key;
  const jwt = state.secrets.jwt_secret;
  const app = state.secrets.companion_app || {};
  const manifest = state.companion_manifest;

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="admin-companion-integration">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex h-4 w-4 items-center justify-center">
          <Link2 className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">Companion App Integration</div>
          <div className="text-[11px] text-muted-foreground">
            Cross-app plumbing between Crown &amp; Rose and the Indie Reader Companion.
            Values below can be set in <strong>Emergent Deployment Secrets</strong> (per environment)
            <em> or</em> overridden here without a redeploy.
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={reload} className="h-7 gap-1 text-muted-foreground"
                data-testid="admin-companion-refresh">
          <RefreshCcw className="h-3 w-3" /> Refresh
        </Button>
      </div>

      {/* iter-reader-sync-health — 14-day ingest sparkline. Sits at
          the top of the card so ops sees pipeline health before the
          secrets/rotation controls below. */}
      <div className="mt-3" data-testid="admin-companion-sync-health-slot">
        <ReaderSyncHealthWidget />
      </div>

      {/* iter-companion-cert-pin — Reader universal-link fingerprint pin. */}
      <ReaderCertPinRow />

      {/* iter-slack-webhook-config — Save / rotate the Slack incoming-webhook URL. */}
      <SlackWebhookRow />

      {/* iter-ai-sync — Push AI-Assisted genres/books/headlines to the Reader. */}
      <AiSyncRow />

      {/* Fresh-key one-time echo. */}
      {freshKey && (
        <div className="mt-3 rounded border border-emerald-500/40 bg-emerald-500/10 p-3"
             data-testid="admin-companion-fresh-key">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
            <KeyRound className="h-3.5 w-3.5" />
            New value for <code>{freshKey.field}</code> — copy now, it will be hidden on refresh.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Input readOnly value={freshKey.value}
                   className="h-8 font-mono-stat text-[11px]"
                   data-testid="admin-companion-fresh-key-value" />
            <Button size="sm" onClick={() => copyToClipboard(freshKey.value)}
                    className="h-8 gap-1.5"
                    data-testid="admin-companion-fresh-key-copy">
              <Copy className="h-3.5 w-3.5" /> Copy
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setFreshKey(null)}
                    className="h-8">Dismiss</Button>
          </div>
        </div>
      )}

      {/* Service key row */}
      <div className="mt-4 rounded border border-border bg-background p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5 text-[12px] font-semibold">
              <Shield className="h-3.5 w-3.5 text-primary" />
              <code>X-Service-Key</code> · Companion Admin Channel
              <StatusChip src={sk.source} configured={sk.configured} />
            </div>
            <div className="text-[10.5px] text-muted-foreground">
              Paste in Companion Secrets as <code>CROWNROSE_SERVICE_KEY</code>.
              {sk.configured
                ? <> A value is configured. Env {sk.env_present ? 'present' : 'unset'}.</>
                : <> No value is set in the DB override or the env — the channel is <strong>locked</strong> (503).</>}
            </div>
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" onClick={rotateSk} disabled={busy}
                    className="h-8 gap-1.5"
                    data-testid="admin-companion-sk-rotate">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
              Rotate
            </Button>
            {sk.db_override_set && (
              <Button size="sm" variant="ghost" onClick={clearSk} disabled={busy}
                      className="h-8 gap-1.5 text-muted-foreground"
                      data-testid="admin-companion-sk-clear">
                <Trash2 className="h-3.5 w-3.5" /> Clear override
              </Button>
            )}
          </div>
        </div>
        <div className="mt-2 flex gap-2">
          <Input type="password" value={skInput}
                 onChange={(e) => setSkInput(e.target.value)}
                 placeholder={sk.configured ? 'paste new key to replace' : 'paste service key'}
                 className="h-8 max-w-xl font-mono-stat text-[11px]"
                 data-testid="admin-companion-sk-input" />
          <Button size="sm" onClick={saveSk} disabled={busy || skInput.trim().length < 16}
                  className="h-8 gap-1.5"
                  data-testid="admin-companion-sk-save">
            <Save className="h-3.5 w-3.5" /> Save
          </Button>
        </div>
      </div>

      {/* JWT secret row */}
      <div className="mt-3 rounded border border-border bg-background p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5 text-[12px] font-semibold">
              <KeyRound className="h-3.5 w-3.5 text-primary" />
              <code>JWT Signing Secret</code> · Author Routes
              <StatusChip src={jwt.source} configured={jwt.configured} />
            </div>
            <div className="text-[10.5px] text-muted-foreground">
              Shared HMAC key for tokens minted at <code>/api/companion-admin/authors/{'{'}id{'}'}/mint-jwt</code>.
              {jwt.configured
                ? <> A value is configured. Env {jwt.env_present ? 'present' : 'unset'}.</>
                : <> Falls back to <code>ADMIN_PASSWORD</code> in absolute-emergency mode.</>}
            </div>
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" onClick={rotateJwt} disabled={busy}
                    className="h-8 gap-1.5"
                    data-testid="admin-companion-jwt-rotate">
              <RefreshCcw className="h-3.5 w-3.5" /> Rotate
            </Button>
            {jwt.db_override_set && (
              <Button size="sm" variant="ghost" onClick={clearJwt} disabled={busy}
                      className="h-8 gap-1.5 text-muted-foreground"
                      data-testid="admin-companion-jwt-clear">
                <Trash2 className="h-3.5 w-3.5" /> Clear override
              </Button>
            )}
          </div>
        </div>
        <div className="mt-2 flex gap-2">
          <Input type="password" value={jwtInput}
                 onChange={(e) => setJwtInput(e.target.value)}
                 placeholder={jwt.configured ? 'paste new secret to replace' : 'paste JWT secret'}
                 className="h-8 max-w-xl font-mono-stat text-[11px]"
                 data-testid="admin-companion-jwt-input" />
          <Button size="sm" onClick={saveJwt} disabled={busy || jwtInput.trim().length < 16}
                  className="h-8 gap-1.5"
                  data-testid="admin-companion-jwt-save">
            <Save className="h-3.5 w-3.5" /> Save
          </Button>
        </div>
      </div>

      {/* Outbound → Indie Reader Location */}
      <div className="mt-3 rounded border border-border bg-background p-3"
           data-testid="admin-companion-outbound">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold">
          <Globe className="h-3.5 w-3.5 text-primary" />
          Indie Reader Location · Outbound API
          {app.configured
            ? <span className="rounded-full bg-emerald-500/15 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">CONFIGURED</span>
            : <span className="rounded-full bg-red-500/15 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider text-red-600">INCOMPLETE</span>}
        </div>
        <div className="mt-0.5 text-[10.5px] text-muted-foreground">
          The base URL + API key Crown &amp; Rose uses to call <em>into</em> the Indie Reader / Companion app (author push, book sync, cover uploads).
          Env fallbacks: <code>INDIE_READER_API</code> · <code>INDIE_READER_SERVICE_KEY</code>.
        </div>

        {/* URL row */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Label className="w-32 text-[11px] font-semibold text-muted-foreground">Indie Reader URL</Label>
          <StatusChip src={app.url_source} configured={!!app.url} />
          {app.url
            ? <code className="rounded bg-muted px-1.5 py-0.5 font-mono-stat text-[10.5px]">{app.url}</code>
            : <span className="text-[10.5px] italic text-muted-foreground">unset — outbound calls will 503</span>}
        </div>
        <div className="mt-1.5 flex gap-2">
          <Input
            value={appUrlInput}
            onChange={(e) => setAppUrlInput(e.target.value)}
            placeholder="https://reader.crownandrose.com/api"
            className="h-8 max-w-xl font-mono-stat text-[11px]"
            data-testid="admin-companion-app-url-input"
          />
          <Button size="sm" onClick={saveAppUrl}
                  disabled={busy || !/^https?:\/\/.+/.test(appUrlInput.trim())}
                  className="h-8 gap-1.5"
                  data-testid="admin-companion-app-url-save">
            <Save className="h-3.5 w-3.5" /> Save URL
          </Button>
          {app.url_db_override_set && (
            <Button size="sm" variant="ghost" onClick={clearAppUrl} disabled={busy}
                    className="h-8 gap-1.5 text-muted-foreground"
                    data-testid="admin-companion-app-url-clear">
              <Trash2 className="h-3.5 w-3.5" /> Clear
            </Button>
          )}
        </div>

        {/* API key row */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Label className="w-32 text-[11px] font-semibold text-muted-foreground">Indie Reader API Key</Label>
          <StatusChip src={app.key_source} configured={!!app.key_configured} />
          {app.key_configured
            ? <span className="text-[10.5px] text-muted-foreground">configured</span>
            : <span className="text-[10.5px] italic text-muted-foreground">unset</span>}
        </div>
        <div className="mt-1.5 flex gap-2">
          <Input
            type="password"
            value={appKeyInput}
            onChange={(e) => setAppKeyInput(e.target.value)}
            placeholder="paste the INDIE_READER_SERVICE_KEY value"
            className="h-8 max-w-xl font-mono-stat text-[11px]"
            data-testid="admin-companion-app-key-input"
          />
          <Button size="sm" onClick={saveAppKey}
                  disabled={busy || appKeyInput.trim().length < 16}
                  className="h-8 gap-1.5"
                  data-testid="admin-companion-app-key-save">
            <Save className="h-3.5 w-3.5" /> Save Key
          </Button>
          {app.key_db_override_set && (
            <Button size="sm" variant="ghost" onClick={clearAppKey} disabled={busy}
                    className="h-8 gap-1.5 text-muted-foreground"
                    data-testid="admin-companion-app-key-clear">
              <Trash2 className="h-3.5 w-3.5" /> Clear
            </Button>
          )}
        </div>

        {/* Diagnostics */}
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={runAppDiag}
                  disabled={busy || !app.configured}
                  className="h-8 gap-1.5"
                  data-testid="admin-companion-app-diagnostics">
            <Activity className="h-3.5 w-3.5" /> Test Indie Reader connection
          </Button>
          {appDiag && (
            <span className={`text-[10.5px] font-semibold ${appDiag.ok ? 'text-emerald-600' : 'text-red-600'}`}
                  data-testid="admin-companion-app-diag-result">
              {appDiag.ok ? '✓ ' : '✗ '}
              [{appDiag.stage}] {appDiag.message}
            </span>
          )}
        </div>
      </div>

      {/* Companion manifest snapshot */}
      <div className="mt-3 rounded border border-border bg-background p-3"
           data-testid="admin-companion-manifest">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold">
          <Radio className="h-3.5 w-3.5 text-primary" />
          Companion Manifest
          {manifest
            ? <span className="rounded-full bg-emerald-500/15 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">v{manifest.version}</span>
            : <span className="rounded-full bg-muted px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">not yet posted</span>}
        </div>
        {manifest ? (
          <div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
            <div>Base URL: <code>{manifest.base_url}</code></div>
            <div>Endpoints: <strong>{state.companion_manifest_endpoint_count}</strong></div>
            <div>Subscribed events: <strong>{state.companion_manifest_pushed_events}</strong>
              {(manifest.events_subscribed || []).length > 0 && (
                <> — {manifest.events_subscribed.slice(0, 6).join(', ')}
                    {manifest.events_subscribed.length > 6 && '…'}
                </>
              )}
            </div>
            <div>Active webhook subscriptions: <strong>{state.active_webhook_count}</strong></div>
            <div>Last updated: {manifest.updated_at}</div>
          </div>
        ) : (
          <div className="mt-1 text-[11px] text-muted-foreground">
            The Companion should <code>PUT /api/companion-admin/companion-manifest</code> on boot
            to advertise its endpoints and subscribe to events.
          </div>
        )}
        <div className="mt-2 flex gap-2">
          <Button size="sm" variant="outline" onClick={pingCompanion}
                  disabled={busy || !manifest}
                  className="h-8 gap-1.5"
                  data-testid="admin-companion-ping">
            <Zap className="h-3.5 w-3.5" /> Ping Companion
          </Button>
        </div>
      </div>

      {/* Fire test event */}
      <div className="mt-3 rounded border border-border bg-background p-3">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold">
          <Rocket className="h-3.5 w-3.5 text-primary" />
          Fire test event
        </div>
        <div className="mt-1 text-[10.5px] text-muted-foreground">
          Fans out through the real webhook plumbing (HMAC-signed
          POST to the Companion's <code>webhook_url</code>). Use to verify
          the signature verifier on the Companion side.
        </div>
        <div className="mt-2 flex gap-2">
          <select
            value={testEvent}
            onChange={(e) => setTestEvent(e.target.value)}
            className="h-8 rounded-md border border-border bg-background px-2 text-[11.5px]"
            data-testid="admin-companion-event-select"
          >
            {['book.published','price.changed','offer.created',
              'author.posted','marketing.updated','wip.sample_updated',
              'author.broadcast'].map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
          <Button size="sm" onClick={fireEvent} disabled={busy}
                  className="h-8 gap-1.5"
                  data-testid="admin-companion-fire-event">
            <Rocket className="h-3.5 w-3.5" /> Fire
          </Button>
        </div>
      </div>

      <details className="mt-3 rounded border border-dashed border-border p-2 text-[11px]">
        <summary className="cursor-pointer text-muted-foreground">
          Where to paste these on each side
        </summary>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
          <li><strong>Companion Secrets tab</strong>: <code>CROWNROSE_API_URL</code> = <code>https://crownnrose.com</code>, <code>CROWNROSE_SERVICE_KEY</code> = the X-Service-Key value above.</li>
          <li><strong>Our Emergent Deployment Secrets (Production)</strong>: <code>COMPANION_ADMIN_SERVICE_KEY</code>, <code>COMPANION_API_JWT_SECRET</code> (inbound), <code>INDIE_READER_API</code>, <code>INDIE_READER_SERVICE_KEY</code> (outbound) — same values as here.</li>
          <li><strong>Our Emergent Preview/Test Secrets</strong>: same four vars — a distinct rotation from prod so preview traffic can't affect live subscribers.</li>
          <li>Rotating here overrides the env until you <em>Clear override</em>. Keeping the env populated as a floor means a wiped DB never locks the channel.</li>
        </ul>
      </details>
    </div>
  );
}
